import type { ReactNode } from "react";
import { useNavigate, useRouter } from "@tanstack/react-router";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface ErrorStateProps {
  /** The status code shown large at the top, e.g. "404". */
  code: string;
  title: string;
  description?: ReactNode;
  /** Replaces the default Go Back / Back to Home buttons. */
  action?: ReactNode;
  /** Fill the viewport. Set false to centre inside an existing layout. */
  fullScreen?: boolean;
}

/**
 * Shared layout for the app's error screens (401, 404). Keeping both on one
 * component stops the two pages drifting apart visually.
 */
export const ErrorState = ({
  code,
  title,
  description,
  action,
  fullScreen = true,
}: ErrorStateProps) => {
  const navigate = useNavigate();
  const { history } = useRouter();

  return (
    <div className={cn(fullScreen ? "h-svh" : "flex-1 self-stretch")}>
      <div className="m-auto flex h-full w-full flex-col items-center justify-center gap-2">
        <h1 className="text-[7rem] leading-tight font-bold text-primary">
          {code}
        </h1>
        <span className="font-medium">{title}</span>
        {description && (
          <p className="text-muted-foreground text-center">{description}</p>
        )}
        <div className="mt-6 flex gap-4">
          {action ?? (
            <>
              <Button variant="outline" onClick={() => history.go(-1)}>
                Go Back
              </Button>
              <Button onClick={() => navigate({ to: "/" })}>
                Back to Home
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ErrorState;
